import React, { createContext, useContext, useEffect, useState } from "react";

// Lets a page's mobile search row "claim" the user-menu avatar so it renders
// inline (left of the search bar) instead of in the global header. The header
// hides its own mobile avatar while the slot is claimed, so there's never two.
// Pages without a search row don't claim it → the header keeps showing it,
// preserving global logout access on every mobile screen.
//
// `scrollHidden` is a SEPARATE reason to hide the avatar — a docked nav bar
// taking over the top edge. Unlike `claimed`, it does NOT remove the top spacer
// (MobileTopGap), so toggling it on scroll can't jump the layout / bounce scroll.
const MobileUserSlotContext = createContext<{
  claimed: boolean;
  setClaimed: (v: boolean) => void;
  scrollHidden: boolean;
  setScrollHidden: (v: boolean) => void;
}>({
  claimed: false,
  setClaimed: () => {},
  scrollHidden: false,
  setScrollHidden: () => {},
});

export const MobileUserSlotProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [claimed, setClaimed] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);
  return (
    <MobileUserSlotContext.Provider
      value={{ claimed, setClaimed, scrollHidden, setScrollHidden }}
    >
      {children}
    </MobileUserSlotContext.Provider>
  );
};

// MobileTopGap reads this — the gap is dropped only when a search row claims the
// slot (it provides its own top spacing). NOT affected by scroll-hiding.
export const useMobileUserSlotClaimed = () =>
  useContext(MobileUserSlotContext).claimed;

// Header reads this to hide its mobile avatar — claimed by a search row OR
// scroll-hidden by a docked nav bar.
export const useMobileAvatarHidden = () => {
  const c = useContext(MobileUserSlotContext);
  return c.claimed || c.scrollHidden;
};

// Docked nav bar uses this to hide the avatar on scroll (no layout side effects).
export const useSetMobileScrollHidden = () =>
  useContext(MobileUserSlotContext).setScrollHidden;

// Search row calls this on mount to take ownership of the mobile avatar.
export const useClaimMobileUserSlot = () => {
  const { setClaimed } = useContext(MobileUserSlotContext);
  useEffect(() => {
    setClaimed(true);
    return () => setClaimed(false);
  }, [setClaimed]);
};
